import { Controller } from '@hotwired/stimulus';

// The blocked site never reaches the server. Extension 0.3.5+ hands it to this page through the
// extension itself, so it is nowhere in the URL — not a path segment, not a query param, not a
// fragment. That means nothing the server renders can name it, and everything about it is filled
// in here instead (UNS-4990).
//
// Older extensions still redirect with ?domain= and will for a long time, so the query string is
// read as a fallback. Those versions send no category and no URL, so they get the domain alone.
const FROM_EXTENSION = 'unscreen-extension:blocked-page';
const TO_EXTENSION = 'unscreen-site-blocker:request';

export default class extends Controller {
  static targets = ['pill', 'domain', 'category', 'settingsLink', 'continueButton'];
  static values = { categoryNames: Object };

  connect() {
    this.onMessage = (event) => {
      if (event.source !== window || event.origin !== window.location.origin) return;
      if (event.data?.source !== FROM_EXTENSION) return;

      this.render(event.data.blockedPage, event.data.blocking);
    };
    window.addEventListener('message', this.onMessage);

    // Nothing read off the URL can tell us whether the site is still blocked, so the way back
    // stays hidden until the extension says otherwise.
    this.render(this.blockedPageFromUrl(), true);

    // The content script can load either side of this controller, so ask as well as listen.
    window.postMessage({ source: TO_EXTENSION }, window.location.origin);
  }

  disconnect() {
    window.removeEventListener('message', this.onMessage);
  }

  // Navigated in JS rather than through an href: session replay records DOM attributes even
  // though it masks text, so an href holding the blocked URL would be captured in the replay.
  goToSite(event) {
    event.preventDefault();
    if (this.blockedUrl) window.location.href = this.blockedUrl;
  }

  // Same reason as goToSite: the deep-linked settings path names the blocked site, so it is built
  // on the click rather than written into the link's href, where the replay would record it. With
  // no domain to add, the link's own href stands and lands on the settings page undeep-linked.
  //
  // The href's query string is carried across too: it tells the router this is an internal hop and
  // not an extension popup click, which it would otherwise tag as one (UNS-5134).
  goToSettings(event) {
    if (!this.blockedDomain) return;

    event.preventDefault();
    const target = this.settingsLinkTarget;
    window.open(`${target.pathname}/${encodeURIComponent(this.blockedDomain)}${target.search}`, '_blank', 'noopener');
  }

  render(blockedPage, blocking) {
    if (!blockedPage?.domain) return;

    this.domainTarget.textContent = blockedPage.domain;
    this.pillTarget.classList.remove('info-domain-pill--pending');
    this.renderCategory(blockedPage.category);

    // Held for the click rather than written into the settings link's href (see goToSettings).
    // Going there does put the domain in a request path, but only once the user chooses to manage
    // it — the same trip they would make from anywhere else in the app, and the one the :catdom
    // scrubbers already cover.
    this.blockedDomain = blockedPage.domain;

    this.offerWayBack(blockedPage.url, blocking);
  }

  // Own properties only: the slug arrives from the extension or the query string, and a lookup
  // straight off the object would answer `toString` or `constructor` with something from the
  // prototype and print it in the pill.
  renderCategory(slug) {
    const names = this.categoryNamesValue;
    const name = slug && Object.prototype.hasOwnProperty.call(names, slug) ? names[slug] : null;

    // Emptied rather than hidden: the pill's :empty rule collapses it, gap and all.
    this.categoryTarget.textContent = name ? `(${name})` : '';
  }

  // Shown only while the site is not blocked: the extension is asked afresh each time, so this
  // appears the moment the user switches the site off in another tab, and goes away again if
  // they switch it back on. Offering it while still blocked would just bounce them off the
  // blocked site straight back to this page.
  //
  // Default-deny — anything other than an explicit false keeps it hidden — because the fallback
  // for older extensions can't answer the question at all.
  offerWayBack(url, blocking) {
    this.blockedUrl = this.navigableUrl(url);
    this.continueButtonTarget.classList.toggle('d-none', !this.blockedUrl || blocking !== false);
  }

  // Only http(s), and only ever a URL the extension handed over. Anything read off this page's
  // own address would let someone hand out a /site_blocker link that turns the button into an
  // open redirect wearing our chrome — and a javascript: URL would run in our own origin.
  navigableUrl(url) {
    if (!url) return null;

    let parsed;
    try {
      parsed = new URL(url);
    } catch {
      return null;
    }

    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : null;
  }

  blockedPageFromUrl() {
    const fromFragment = new URLSearchParams(window.location.hash.slice(1));
    const params = fromFragment.has('domain') ? fromFragment : new URLSearchParams(window.location.search);
    const domain = params.get('domain');

    // No url: a way back is only offered for a URL the extension handed over out of band.
    return domain ? { domain, category: params.get('category'), url: null } : null;
  }
}
